import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import { useFonts, useFontDetail, useFontUpload, useCollections, useTags, useStats } from '../hooks/useData.js';
import { users as usersApi, fonts as fontsApi, activations as activationsApi, collections as collectionsApi, googlefonts as googlefontsApi, adobefonts as adobefontsApi } from '../api/client.js';
import DetailPanel from './detail/DetailPanel.jsx';
import ComparisonPanel from './ComparisonPanel.jsx';
import GoogleFontsBrowser from './GoogleFontsBrowser.jsx';
import AdobeFontsBrowser from './AdobeFontsBrowser.jsx';
import CSSPlayground from './CSSPlayground.jsx';
import DuplicatesPanel from './DuplicatesPanel.jsx';
import HealthDashboard from './HealthDashboard.jsx';
import SmartSearch from './SmartSearch.jsx';
import UserManagement from './UserManagement.jsx';
import BrandKitsPanel from './BrandKitsPanel.jsx';
import DuplicatePrompt from './DuplicatePrompt.jsx';
import { ensureLoaded } from '../hooks/useFontFaceLoader.js';

import HeaderBar from './fontvault/HeaderBar.jsx';
import Sidebar from './fontvault/Sidebar.jsx';
import Toolbar from './fontvault/Toolbar.jsx';
import FontGrid from './fontvault/FontGrid.jsx';
import UploadZone from './fontvault/UploadZone.jsx';
import BulkActionBar from './fontvault/BulkActionBar.jsx';
import ExternalResults from './fontvault/ExternalResults.jsx';

const SAMPLE_TEXT = 'The quick brown fox jumps over the lazy dog';
const FONT_EXTS = ['.otf', '.ttf', '.woff', '.woff2'];
const LICENSE_EXTS = ['.txt', '.pdf', '.html', '.htm', '.rtf', '.md'];

async function sha256File(file) {
  const buf = await file.arrayBuffer();
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function getAllFilesFromEntry(entry) {
  if (entry.isFile) {
    return new Promise(resolve => entry.file(f => resolve([f]), () => resolve([])));
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const allEntries = await new Promise(resolve => {
      const collected = [];
      function read() {
        reader.readEntries(batch => {
          if (batch.length === 0) resolve(collected);
          else { collected.push(...batch); read(); }
        }, () => resolve(collected));
      }
      read();
    });
    const nested = await Promise.all(allEntries.map(getAllFilesFromEntry));
    return nested.flat();
  }
  return [];
}

export default function FontVault() {
  const { user } = useAuth();
  const { theme, isDark, toggle: toggleTheme } = useTheme();

  // ─── UI State ──────────────────────────────────────
  const [activeCollection, setActiveCollection] = useState('All Fonts');
  const [activeCollectionId, setActiveCollectionId] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFontId, setSelectedFontId] = useState(null);
  const [previewText, setPreviewText] = useState(SAMPLE_TEXT);
  const [previewSize, setPreviewSize] = useState(32);
  const [viewMode, setViewMode] = useState('grid');
  const [showUpload, setShowUpload] = useState(false);
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterLicense, setFilterLicense] = useState('All');
  const [showActivatedOnly, setShowActivatedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [teamMembers, setTeamMembers] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const searchTimeout = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [activeTag, setActiveTag] = useState(null);
  const [showDetailTagDropdown, setShowDetailTagDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [showFavorites, setShowFavorites] = useState(false);
  const [favCount, setFavCount] = useState(0);
  const [showMyFonts, setShowMyFonts] = useState(false);
  const [activeLetter, setActiveLetter] = useState(null);
  const [letterCounts, setLetterCounts] = useState({});
  const [selectedFontIds, setSelectedFontIds] = useState(new Set());

  // Overlays
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonFontIds, setComparisonFontIds] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [showGoogleBrowser, setShowGoogleBrowser] = useState(false);
  const [showAdobeBrowser, setShowAdobeBrowser] = useState(false);
  const [showPlayground, setShowPlayground] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [showHealth, setShowHealth] = useState(false);
  const [showSmartSearch, setShowSmartSearch] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showBrandKits, setShowBrandKits] = useState(false);

  // External catalog search
  const [externalResults, setExternalResults] = useState({ google: [], adobe: [] });
  const [externalLoading, setExternalLoading] = useState(false);
  const [importingFont, setImportingFont] = useState(null);
  const [extFontsReady, setExtFontsReady] = useState({});

  // Debounce search
  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  // Fetch Google + Adobe catalog results when searching
  useEffect(() => {
    if (!debouncedSearch) {
      setExternalResults({ google: [], adobe: [] });
      return;
    }
    setExternalLoading(true);
    Promise.all([
      googlefontsApi.list({ search: debouncedSearch, limit: 8 }).catch(() => ({ fonts: [] })),
      adobefontsApi.list({ search: debouncedSearch, limit: 8 }).catch(() => ({ fonts: [] })),
    ]).then(([gData, aData]) => {
      setExternalResults({ google: gData.fonts || [], adobe: aData.fonts || [] });
    }).finally(() => setExternalLoading(false));
  }, [debouncedSearch]);

  // ─── Data Hooks ──────────────────────────────────────
  const fontParams = {
    search: debouncedSearch || undefined,
    category: filterCategory !== 'All' ? filterCategory : undefined,
    collection: activeCollection !== 'All Fonts' ? activeCollection : undefined,
    activated: showActivatedOnly ? 'true' : undefined,
    favorite: showFavorites ? 'true' : undefined,
    my_fonts: showMyFonts ? 'true' : undefined,
    tag: activeTag || undefined,
    startsWith: activeLetter || undefined,
    license_type: filterLicense !== 'All' ? filterLicense : undefined,
    sort: sortBy,
    order: sortOrder,
    page,
    limit: 50,
  };

  const {
    fonts, pagination, loading: fontsLoading, error: fontsError,
    refetch: refetchFonts, toggleFavorite, toggleActivation, deleteFont, rateFont,
  } = useFonts(fontParams);

  const { font: selectedFont, loading: detailLoading, refetch: refetchDetail } = useFontDetail(selectedFontId);
  const { upload, uploading, progress: uploadProgress, error: uploadError } = useFontUpload();
  const {
    collections, refetch: refetchCollections,
    create: createCollection, update: updateCollection, remove: removeCollection,
    addFonts: addFontsToCollection,
  } = useCollections();
  const {
    tags, refetch: refetchTags,
    create: createTag, remove: removeTag,
    addToFont: addTagsToFont, removeFromFont: removeTagFromFont,
  } = useTags();
  const { stats, setActiveCount, refresh: refreshStats } = useStats();

  // Load team members
  useEffect(() => {
    usersApi.list().then(data => setTeamMembers(data.users || [])).catch(() => {});
  }, []);

  // Load alphabet counts
  const fetchAlphabet = useCallback(() => {
    fontsApi.alphabet().then(data => {
      const map = {};
      for (const row of data.letters) map[row.letter] = row.count;
      setLetterCounts(map);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchAlphabet(); }, [fetchAlphabet]);
  useEffect(() => { if (!fontsLoading) fetchAlphabet(); }, [pagination.total, fontsLoading, fetchAlphabet]);

  useEffect(() => {
    if (stats?.favorites_count !== undefined) setFavCount(stats.favorites_count);
  }, [stats]);

  // Load Google Fonts CSS for preview
  useEffect(() => {
    if (fonts.length === 0) return;
    const googleFonts = fonts.filter(f => f.source === 'google-fonts');
    const existingLink = document.querySelector('link[data-fontvault]');
    if (googleFonts.length === 0) {
      if (existingLink) existingLink.remove();
      return;
    }
    const families = [...new Set(googleFonts.map(f => f.family))];
    const encoded = families.map(f => f.replace(/ /g, '+')).join('&family=');
    if (existingLink) existingLink.remove();
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${encoded}&display=swap`;
    link.rel = 'stylesheet';
    link.dataset.fontvault = 'true';
    document.head.appendChild(link);
  }, [fonts]);

  // Load Adobe Fonts CSS
  useEffect(() => {
    if (document.querySelector('link[data-fontvault-adobe]')) return;
    const link = document.createElement('link');
    link.href = 'https://use.typekit.net/xla3sgm.css';
    link.rel = 'stylesheet';
    link.dataset.fontvaultAdobe = 'true';
    document.head.appendChild(link);
  }, []);

  // Load Google external results CSS
  useEffect(() => {
    const old = document.querySelector('link[data-fontvault-ext]');
    if (old) old.remove();

    const families = externalResults.google;
    if (families.length === 0) {
      setExtFontsReady(prev => {
        const next = { ...prev };
        Object.keys(next).filter(k => k.startsWith('g:')).forEach(k => delete next[k]);
        return next;
      });
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.dataset.fontvaultExt = 'true';
    const familyParam = families.map(f => f.family.replace(/ /g, '+')).join('&family=');
    link.href = `https://fonts.googleapis.com/css2?family=${familyParam}&display=swap`;
    document.head.appendChild(link);

    const snapshot = [...families];
    function onCssLoad() {
      snapshot.forEach(async (font, idx) => {
        try { await document.fonts.load(`1em '${font.family}'`); } catch {}
        setExtFontsReady(prev => ({ ...prev, [`g:${idx}`]: true }));
      });
    }
    link.addEventListener('load', onCssLoad);
    link.addEventListener('error', () => {
      snapshot.forEach((_, idx) =>
        setExtFontsReady(prev => ({ ...prev, [`g:${idx}`]: true }))
      );
    });

    return () => { document.querySelector('link[data-fontvault-ext]')?.remove(); };
  }, [externalResults.google]);

  // IntersectionObserver: lazy-load @font-face for non-Google fonts
  const observerRef = useRef(null);
  const fontsMapRef = useRef(new Map());

  useEffect(() => {
    fontsMapRef.current = new Map(fonts.map(f => [String(f.id), f]));
  }, [fonts]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const fontId = entry.target.dataset.fontId;
          const font = fontsMapRef.current.get(fontId);
          if (font) ensureLoaded(font);
        }
      }
    }, { rootMargin: '200px' });
    const cards = document.querySelectorAll('[data-font-id]');
    cards.forEach(card => observerRef.current.observe(card));
    return () => observerRef.current?.disconnect();
  }, [fonts]);

  // ─── Upload Handlers ────────────────────────────────
  const doUpload = useCallback(async (fontFiles, licenseFiles, options = {}) => {
    try {
      await upload(fontFiles, undefined, licenseFiles, options);
      refetchFonts();
      refetchCollections();
      setShowUpload(false);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  }, [upload, refetchFonts, refetchCollections]);

  const handleFiles = useCallback(async (fileList) => {
    const all = Array.from(fileList);
    const fontFiles = all.filter(f => FONT_EXTS.some(ext => f.name.toLowerCase().endsWith(ext)));
    const licenseFiles = all.filter(f => LICENSE_EXTS.some(ext => f.name.toLowerCase().endsWith(ext)));
    if (fontFiles.length === 0) return;

    try {
      const fileHashes = new Map();
      await Promise.all(fontFiles.map(async f => {
        fileHashes.set(f, await sha256File(f));
      }));
      const hashList = fontFiles.map(f => ({ name: f.name, hash: fileHashes.get(f) }));
      const { duplicates } = await fontsApi.checkDuplicates(hashList);
      if (duplicates.length > 0) {
        setPendingUpload({ fontFiles, licenseFiles, fileHashes, duplicates });
        return;
      }
    } catch {
      // If check fails, proceed with upload
    }

    await doUpload(fontFiles, licenseFiles, {});
  }, [doUpload]);

  const handleDuplicateSkip = useCallback(async () => {
    const { fontFiles, licenseFiles, fileHashes, duplicates } = pendingUpload;
    setPendingUpload(null);
    const dupHashes = new Set(duplicates.map(d => d.hash));
    const filtered = fontFiles.filter(f => !dupHashes.has(fileHashes.get(f)));
    if (filtered.length === 0) { setShowUpload(false); return; }
    await doUpload(filtered, licenseFiles, {});
  }, [pendingUpload, doUpload]);

  const handleDuplicateReplace = useCallback(async () => {
    const { fontFiles, licenseFiles, duplicates } = pendingUpload;
    setPendingUpload(null);
    const replaceIds = [...new Set(duplicates.map(d => d.existingId))];
    await doUpload(fontFiles, licenseFiles, { replaceIds });
  }, [pendingUpload, doUpload]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.items?.length > 0) {
      const entries = [...e.dataTransfer.items]
        .filter(item => item.kind === 'file')
        .map(item => item.webkitGetAsEntry?.())
        .filter(Boolean);
      if (entries.some(en => en.isDirectory)) {
        const fileArrays = await Promise.all(entries.map(getAllFilesFromEntry));
        handleFiles(fileArrays.flat().filter(Boolean));
        return;
      }
    }
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  // ─── Bulk Handlers ────────────────────────────────
  const toggleSelectFont = useCallback((fontId) => {
    setSelectedFontIds(prev => {
      const next = new Set(prev);
      if (next.has(fontId)) next.delete(fontId);
      else next.add(fontId);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    if (selectedFontIds.size === fonts.length) setSelectedFontIds(new Set());
    else setSelectedFontIds(new Set(fonts.map(f => f.id)));
  }, [fonts, selectedFontIds.size]);

  const clearSelection = useCallback(() => {
    setSelectedFontIds(new Set());
  }, []);

  const handleBulkAddToCollection = useCallback(async (collectionId) => {
    try {
      await addFontsToCollection(collectionId, [...selectedFontIds]);
      refetchFonts();
      clearSelection();
    } catch (err) { console.error('Bulk add to collection failed:', err); }
  }, [selectedFontIds, addFontsToCollection, refetchFonts, clearSelection]);

  const handleBulkTag = useCallback(async (tagId) => {
    try {
      await fontsApi.bulkTag([...selectedFontIds], [tagId]);
      refetchFonts();
      refetchTags();
      clearSelection();
    } catch (err) { console.error('Bulk tag failed:', err); }
  }, [selectedFontIds, refetchFonts, refetchTags, clearSelection]);

  const handleImportExternal = useCallback(async (font, source) => {
    setImportingFont(font.family);
    try {
      if (source === 'google') {
        await googlefontsApi.import({ family: font.family, category: font.category, variants: font.variants });
      } else {
        await adobefontsApi.import({ family: font.family, category: font.category, styleCount: font.styleCount });
      }
      refetchFonts();
      setExternalResults(prev => ({ ...prev, [source]: prev[source].filter(f => f.family !== font.family) }));
    } catch (err) {
      if (err.status === 409) {
        setExternalResults(prev => ({ ...prev, [source]: prev[source].filter(f => f.family !== font.family) }));
      }
    } finally {
      setImportingFont(null);
    }
  }, [refetchFonts]);

  const handleBulkActivate = useCallback(async (active) => {
    try {
      await activationsApi.bulk([...selectedFontIds], active);
      refetchFonts();
      refreshStats();
      clearSelection();
    } catch (err) { console.error('Bulk activate failed:', err); }
  }, [selectedFontIds, refetchFonts, refreshStats, clearSelection]);

  const handleBulkDelete = useCallback(async () => {
    if (!confirm(`Delete ${selectedFontIds.size} font(s)? This cannot be undone.`)) return;
    try {
      await fontsApi.bulkDelete([...selectedFontIds]);
      refetchFonts();
      refetchCollections();
      clearSelection();
    } catch (err) { console.error('Bulk delete failed:', err); }
  }, [selectedFontIds, refetchFonts, refetchCollections, clearSelection]);

  // ─── Detail Panel Tag Handlers ────────────────────
  const handleAddTagToFont = useCallback(async (tagId) => {
    if (!selectedFontId) return;
    try {
      await addTagsToFont(selectedFontId, [tagId]);
      setShowDetailTagDropdown(false);
      refetchDetail();
      refetchFonts();
      refetchTags();
    } catch (err) { console.error('Add tag failed:', err); }
  }, [selectedFontId, addTagsToFont, refetchDetail, refetchFonts, refetchTags]);

  const handleRemoveTagFromFont = useCallback(async (tagId) => {
    if (!selectedFontId) return;
    try {
      await removeTagFromFont(selectedFontId, tagId);
      refetchDetail();
      refetchFonts();
      refetchTags();
    } catch (err) { console.error('Remove tag failed:', err); }
  }, [selectedFontId, removeTagFromFont, refetchDetail, refetchFonts, refetchTags]);

  // ─── Sort Handler ────────────────────────────────
  const handleSortChange = useCallback((value) => {
    const [field, dir] = value.split(':');
    setSortBy(field);
    setSortOrder(dir);
    setPage(1);
  }, []);

  // ─── Sidebar Navigation ──────────────────────────
  const handleSidebarNav = useCallback((type, value, id) => {
    setActiveCollection(type === 'collection' ? value : 'All Fonts');
    setActiveCollectionId(type === 'collection' ? (id || 0) : 0);
    setShowFavorites(type === 'favorites');
    setShowMyFonts(type === 'myFonts');
    setActiveTag(type === 'tag' ? value : null);
    setActiveLetter(null);
    setPage(1);
  }, []);

  const handleActivateCollection = useCallback(async (collectionId) => {
    try {
      const data = await collectionsApi.fontIds(collectionId);
      if (data.fontIds?.length > 0) {
        await activationsApi.bulk(data.fontIds, true);
        refetchFonts();
        refreshStats();
      }
    } catch (err) { console.error('Activate collection failed:', err); }
  }, [refetchFonts, refreshStats]);

  // ─── Comparison Mode ──────────────────────────
  const toggleCompareFont = useCallback((fontId) => {
    setComparisonFontIds(prev => {
      if (prev.includes(fontId)) return prev.filter(id => id !== fontId);
      if (prev.length >= 3) return prev;
      return [...prev, fontId];
    });
  }, []);

  const launchComparison = useCallback(() => {
    if (comparisonFontIds.length >= 2) setShowComparison(true);
  }, [comparisonFontIds]);

  const handleDeleteFont = useCallback((fontId) => {
    deleteFont(fontId);
    setSelectedFontId(null);
  }, [deleteFont]);

  // ─── Computed ──────────────────────────────────────
  const activatedCount = stats?.my_activations?.count || 0;
  const totalSyncedSize = stats?.my_activations?.total_size_display || '0 B';
  const hasSelection = selectedFontIds.size > 0;

  // ─── Render ──────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', background: theme.bg, color: theme.text,
      fontFamily: "'Manrope', system-ui, sans-serif",
      display: 'flex', flexDirection: 'column',
    }}>
      <HeaderBar
        activatedCount={activatedCount}
        totalSyncedSize={totalSyncedSize}
        teamMembers={teamMembers}
        onToggleTheme={toggleTheme}
        onShowUserManagement={() => setShowUserManagement(true)}
      />

      <div style={{ display: 'flex', flex: 1 }}>
        <Sidebar
          activeCollection={activeCollection}
          showFavorites={showFavorites}
          showMyFonts={showMyFonts}
          activeTag={activeTag}
          favCount={favCount}
          collections={collections}
          tags={tags}
          onNavigate={handleSidebarNav}
          onShowUpload={setShowUpload}
          showUpload={showUpload}
          onCreateCollection={createCollection}
          onUpdateCollection={updateCollection}
          onRemoveCollection={removeCollection}
          onActivateCollection={handleActivateCollection}
          onCreateTag={createTag}
          onRemoveTag={removeTag}
          onShowGoogleBrowser={() => setShowGoogleBrowser(true)}
          onShowAdobeBrowser={() => setShowAdobeBrowser(true)}
          onShowBrandKits={() => setShowBrandKits(true)}
        />

        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <Toolbar
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            filterCategory={filterCategory} setFilterCategory={setFilterCategory}
            filterLicense={filterLicense} setFilterLicense={setFilterLicense}
            sortBy={sortBy} sortOrder={sortOrder} onSortChange={handleSortChange}
            showActivatedOnly={showActivatedOnly} setShowActivatedOnly={setShowActivatedOnly}
            compareMode={compareMode} setCompareMode={setCompareMode}
            comparisonFontIds={comparisonFontIds} setComparisonFontIds={setComparisonFontIds}
            onLaunchComparison={launchComparison}
            viewMode={viewMode} setViewMode={setViewMode}
            activeLetter={activeLetter} setActiveLetter={setActiveLetter}
            letterCounts={letterCounts}
            setPage={setPage}
            onShowSmartSearch={() => setShowSmartSearch(true)}
            onShowPlayground={() => setShowPlayground(true)}
            onShowDuplicates={() => setShowDuplicates(true)}
            onShowHealth={() => setShowHealth(true)}
          />

          {showUpload && (
            <UploadZone
              uploading={uploading}
              uploadProgress={uploadProgress}
              uploadError={uploadError}
              dragOver={dragOver}
              setDragOver={setDragOver}
              onDrop={handleDrop}
              onFiles={handleFiles}
            />
          )}

          <FontGrid
            fonts={fonts}
            fontsLoading={fontsLoading}
            fontsError={fontsError}
            pagination={pagination}
            page={page}
            setPage={setPage}
            previewText={previewText}
            previewSize={previewSize}
            setPreviewSize={setPreviewSize}
            viewMode={viewMode}
            selectedFontId={selectedFontId}
            setSelectedFontId={setSelectedFontId}
            selectedFontIds={selectedFontIds}
            toggleSelectFont={toggleSelectFont}
            selectAllVisible={selectAllVisible}
            compareMode={compareMode}
            comparisonFontIds={comparisonFontIds}
            toggleCompareFont={toggleCompareFont}
            toggleFavorite={toggleFavorite}
            toggleActivation={toggleActivation}
            handleDeleteFont={handleDeleteFont}
            setActiveCount={setActiveCount}
            refetchFonts={refetchFonts}
            hasSelection={hasSelection}
            searchQuery={searchQuery}
          />

          <ExternalResults
            debouncedSearch={debouncedSearch}
            externalLoading={externalLoading}
            externalResults={externalResults}
            previewText={previewText}
            previewSize={previewSize}
            extFontsReady={extFontsReady}
            importingFont={importingFont}
            onImportExternal={handleImportExternal}
          />

          {hasSelection && (
            <BulkActionBar
              selectedFontIds={selectedFontIds}
              collections={collections}
              tags={tags}
              onBulkAddToCollection={handleBulkAddToCollection}
              onBulkTag={handleBulkTag}
              onBulkActivate={handleBulkActivate}
              onBulkDelete={handleBulkDelete}
              onClearSelection={clearSelection}
            />
          )}
        </main>
      </div>

      {/* Detail Panel */}
      {selectedFont && (
        <div style={{
          position: 'fixed', top: 56, right: 0, bottom: 0,
          zIndex: 90, display: 'flex', flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        }}>
          <DetailPanel
            key={selectedFont.id}
            font={selectedFont}
            onClose={() => setSelectedFontId(null)}
            previewText={previewText}
            setPreviewText={setPreviewText}
            toggleActivation={id => toggleActivation(id).then(r => r?.active_count != null && setActiveCount(r.active_count))}
            deleteFont={handleDeleteFont}
            tags={tags}
            onAddTag={handleAddTagToFont}
            onRemoveTag={handleRemoveTagFromFont}
            showDetailTagDropdown={showDetailTagDropdown}
            setShowDetailTagDropdown={setShowDetailTagDropdown}
            collections={collections}
            onSelectFont={setSelectedFontId}
            onRate={rateFont}
            onDownload={(fontId) => fontsApi.collect([fontId]).catch(err => console.error('Download failed:', err))}
          />
        </div>
      )}

      {/* Overlays */}
      {showComparison && (
        <ComparisonPanel fontIds={comparisonFontIds} onClose={() => { setShowComparison(false); setCompareMode(false); setComparisonFontIds([]); }} />
      )}
      {showGoogleBrowser && (
        <GoogleFontsBrowser onClose={() => setShowGoogleBrowser(false)} onImported={() => { refetchFonts(); refetchCollections(); }} />
      )}
      {showAdobeBrowser && (
        <AdobeFontsBrowser onClose={() => setShowAdobeBrowser(false)} onImported={() => { refetchFonts(); refetchCollections(); }} />
      )}
      {showPlayground && <CSSPlayground onClose={() => setShowPlayground(false)} />}
      {showDuplicates && (
        <DuplicatesPanel onClose={() => setShowDuplicates(false)} onDeleteFont={() => { refetchFonts(); refetchCollections(); }} />
      )}
      {showHealth && (
        <HealthDashboard onClose={() => setShowHealth(false)} onSelectFont={(fontId) => { setSelectedFontId(fontId); setShowHealth(false); }} />
      )}
      {showSmartSearch && (
        <SmartSearch onClose={() => setShowSmartSearch(false)} onSelectFont={(fontId) => { setSelectedFontId(fontId); setShowSmartSearch(false); }} />
      )}
      {showUserManagement && (
        <UserManagement onClose={() => { setShowUserManagement(false); usersApi.list().then(data => setTeamMembers(data.users || [])).catch(() => {}); }} />
      )}
      {showBrandKits && (
        <BrandKitsPanel onClose={() => setShowBrandKits(false)} onSelectFont={(fontId) => { setSelectedFontId(fontId); setShowBrandKits(false); }} />
      )}
      {pendingUpload && (
        <DuplicatePrompt duplicates={pendingUpload.duplicates} onSkip={handleDuplicateSkip} onReplace={handleDuplicateReplace} onCancel={() => setPendingUpload(null)} />
      )}
    </div>
  );
}
